/**
 * PDF to Images Utility
 *
 * Uses pdf.js to render PDF pages to images client-side.
 * This allows sending PDF content to Claude Vision without server-side processing.
 *
 * Tiered strategy for page limits:
 * - Documents <= 20 pages: send ALL page images to Claude
 * - Documents 21-50 pages: send first 20 as images + remaining as OCR text
 * - Documents 50+ pages: send OCR text only (too many images would exceed context)
 */

import * as pdfjsLib from 'pdfjs-dist';

// Configure pdf.js worker - use unpkg which mirrors npm packages directly
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/** Threshold constants for tiered PDF handling */
export const PDF_IMAGE_ALL_THRESHOLD = 20;    // <= 20 pages: all as images
export const PDF_HYBRID_THRESHOLD = 50;        // 21-50 pages: first 20 images + rest as text
// > 50 pages: all OCR text only

export interface PDFPageImage {
  pageNumber: number;
  dataUrl: string; // Full data URL (data:image/png;base64,...)
  base64: string; // Just the base64 data
  width: number;
  height: number;
}

export interface PDFConversionOptions {
  /** Maximum number of pages to convert as images (default: 20) */
  maxPages?: number;
  /** Scale factor for rendering (default: 1.5 for good quality) */
  scale?: number;
  /** Image format (default: 'image/png') */
  format?: 'image/png' | 'image/jpeg';
  /** JPEG quality if using jpeg format (default: 0.92) */
  quality?: number;
  /**
   * Stop extracting text once this many characters have been collected. Large
   * text-native PDFs (e.g. a 576-page Westlaw report) are otherwise walked page
   * by page in full — slow enough to hang/OOM — only to be truncated downstream.
   * Undefined = no cap (preserves existing behavior for other callers).
   */
  maxTextChars?: number;
}

/** Result from tiered PDF conversion */
export interface PDFContent {
  /** Page images (may be empty for text-only strategy) */
  images: PDFPageImage[];
  /** OCR text for pages not sent as images (empty if all pages are images) */
  ocrText: string;
  /** Total number of pages in the PDF */
  totalPages: number;
  /** Strategy used: 'all_images' | 'hybrid' | 'text_only' */
  strategy: 'all_images' | 'hybrid' | 'text_only';
}

/**
 * Convert a PDF file to an array of page images
 */
export async function pdfToImages(
  file: File,
  options: PDFConversionOptions = {}
): Promise<PDFPageImage[]> {
  const {
    maxPages = PDF_IMAGE_ALL_THRESHOLD,
    scale = 1.5,
    format = 'image/png',
    quality = 0.92,
  } = options;

  // Read file as ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();

  // Load PDF document
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;

  const numPages = Math.min(pdf.numPages, maxPages);
  const images: PDFPageImage[] = [];

  for (let pageNum = 1; pageNum <= numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale });

    // Create canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Failed to get canvas context');
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Render page to canvas
    // Use type assertion to satisfy pdf.js strict types
    await page.render({
      canvasContext: context as unknown as CanvasRenderingContext2D,
      viewport,
    } as Parameters<typeof page.render>[0]).promise;

    // Convert to data URL
    const dataUrl = canvas.toDataURL(format, quality);
    const base64 = dataUrl.split(',')[1];

    images.push({
      pageNumber: pageNum,
      dataUrl,
      base64,
      width: Math.round(viewport.width),
      height: Math.round(viewport.height),
    });

    // Clean up
    page.cleanup();
  }

  return images;
}

/**
 * Extract text content from specific PDF pages using pdf.js text extraction
 */
async function extractTextFromPages(
  pdf: pdfjsLib.PDFDocumentProxy,
  startPage: number,
  endPage: number,
  maxChars?: number
): Promise<string> {
  const textParts: string[] = [];
  let collected = 0;

  for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
    // Stop once we have enough text — a 576-page doc is truncated downstream
    // anyway, so there's no point walking every page.
    if (maxChars && collected >= maxChars) break;

    let pageText = '';
    try {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      pageText = textContent.items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      page.cleanup();
    } catch {
      // Skip an unreadable page rather than failing the whole document.
      continue;
    }

    if (pageText) {
      textParts.push(`--- Page ${pageNum} ---\n${pageText}`);
      collected += pageText.length;
    }
  }

  return textParts.join('\n\n');
}

/**
 * Convert a PDF using a tiered strategy based on page count:
 * - <= 20 pages: all pages as images
 * - 21-50 pages: first 20 as images + remaining pages as OCR text
 * - 50+ pages: all pages as OCR text only
 */
export async function pdfToContent(
  file: File,
  options: Omit<PDFConversionOptions, 'maxPages'> = {}
): Promise<PDFContent> {
  const {
    scale = 1.5,
    format = 'image/png',
    quality = 0.92,
    maxTextChars,
  } = options;

  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const totalPages = pdf.numPages;

  // Determine strategy based on page count
  if (totalPages <= PDF_IMAGE_ALL_THRESHOLD) {
    // Strategy: all images (small document)
    const images = await pdfToImages(file, { maxPages: totalPages, scale, format, quality });
    return { images, ocrText: '', totalPages, strategy: 'all_images' };
  }

  if (totalPages <= PDF_HYBRID_THRESHOLD) {
    // Strategy: first 20 as images + rest as OCR text
    const images = await pdfToImages(file, { maxPages: PDF_IMAGE_ALL_THRESHOLD, scale, format, quality });
    const ocrText = await extractTextFromPages(pdf, PDF_IMAGE_ALL_THRESHOLD + 1, totalPages, maxTextChars);
    return { images, ocrText, totalPages, strategy: 'hybrid' };
  }

  // Strategy: text only (large document, images would exceed context)
  const ocrText = await extractTextFromPages(pdf, 1, totalPages, maxTextChars);
  return { images: [], ocrText, totalPages, strategy: 'text_only' };
}

/**
 * Get the number of pages in a PDF without fully loading it
 */
export async function getPDFPageCount(file: File): Promise<number> {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  return pdf.numPages;
}

/**
 * Check if a file is a valid PDF
 */
export function isPDFFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}
